import React, {useEffect, useMemo, useState} from "react";
import {handleGraphQLError} from "../utils/graphqlErrorHandler";
import Slide from "@mui/material/Slide";
import useTriStateSort from "../hooks/useTriStateSort";
import PageHeader from "../components/PageHeader";
import BusinessIcon from "@mui/icons-material/Business";
import {formatToLocalTime} from "../utils/dateUtils";
import SearchField from "../components/SearchField";
import ResponsiveSelect from "../components/ResponsiveSelect";
import {Alert, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Divider, IconButton, Paper, Snackbar, Stack, Table, TableBody, TableContainer, TableHead, TablePagination, TableRow, TableSortLabel, TextField, Toolbar, Tooltip, Typography, useMediaQuery} from "@mui/material";
import {gql, useMutation, useQuery} from "@apollo/client";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import RefreshIcon from "@mui/icons-material/Refresh";
import AddIcon from "@mui/icons-material/Add";
import {useTranslation} from "react-i18next";
import EllipsisCell from "../components/EllipsisCell";
import displayValue from "../utils/displayValue";
import sortOptionsByLabel from "../utils/sortOptionsByLabel";
import TablePaginationActions from "../components/TablePaginationActions";

const SlideUp = (props) => <Slide {...props} direction="up"/>;

const PAGINATE_CUSTOMERS = gql`
    query PaginateCustomer($pagination: Pagination!, $sortBy: [SortBy!], $criteria: CustomerCriteria) {
        paginateCustomer(pagination: $pagination, sortBy: $sortBy, criteria: $criteria) {
            pageInfo {
                totalElements
                totalPages
                numberOfElements
                pageNumber
                pageSize
            }
            content {
                id
                name
                type
                reseller {
                    id
                    name
                }
                deleted
                updatedTimestamp
            }
        }
    }
`;


const FILTER_RESELLERS = gql`
    query FilterReseller {
        filterReseller(criteria: { deleted: false }, sortBy: { field: "name", direction: ASC }) {
            id
            name
        }
    }
`;


const CREATE_CUSTOMER = gql`
    mutation CreateCustomer($input: CustomerInput!) {
        createCustomer(input: $input) {
            id
            name
            deleted
        }
    }
`;

const UPDATE_CUSTOMER = gql`
    mutation UpdateCustomer($id: ID!, $input: CustomerInput!) {
        updateCustomer(id: $id, input: $input) {
            id
            name
            updatedTimestamp
        }
    }
`;

const DELETE_CUSTOMER = gql`
    mutation DeleteCustomer($id: ID!) {
        deleteCustomer(id: $id)
    }
`;

const Customer = () => {
    const {t} = useTranslation();
    const role = localStorage.getItem("role") || sessionStorage.getItem("role");
    const hasResellerColumn = role !== "RESELLER";
    const emptyStateColumns = hasResellerColumn ? 5 : 4;


    const [page, setPage] = useState(0);
    const [rowsPerPage, setRpp] = useState(10);
    const {sortFields, handleSort, getSortProps} = useTriStateSort(
        (updated) => {
            refetch({...variables, sortBy: updated});
        }
    );

    const [nameFilter, setNameFilter] = useState("");
    const [typeFilter, setTypeFilter] = useState("");
    const [resellerFilter, setResellerFilter] = useState("");

    const [debouncedFilters, setDebouncedFilters] = useState({
        name: "",
        type: "",
        reseller: "",
    });


    const [createOpen, setCreateOpen] = useState(false);
    const [editOpen, setEditOpen] = useState(false);
    const [deleteOpen, setDeleteOpen] = useState(false);


    const [createForm, setCreateForm] = useState({
        name: "",
        type: "",
        reseller: "",
    });
    const [editForm, setEditForm] = useState({
        id: "",
        name: "",
        type: "",
        reseller: "",
    });
    const [deleteId, setDeleteId] = useState("");


    const [snackbar, setSnackbar] = useState({open: false, message: "", severity: "success"});
    const [createApiError, setCreateApiError] = useState({name: "", general: ""});
    const [editApiError, setEditApiError] = useState({name: "", general: ""});
    const [createError, setCreateError] = useState("");
    const [editError, setEditError] = useState("");
    const [deleteError, setDeleteError] = useState("");

    const isSmall = useMediaQuery("(max-width:600px)");


    const variables = useMemo(() => {
        const criteria = {
            deleted: false,
            ...(debouncedFilters.name ? {name: debouncedFilters.name} : {}),
            ...(debouncedFilters.type ? {type: debouncedFilters.type} : {}),
            ...(debouncedFilters.reseller ? {reseller: debouncedFilters.reseller} : {}),
        };

        return {
            pagination: {page, itemsPerPage: rowsPerPage},
            sortBy: sortFields,
            criteria,
        };
    }, [page, rowsPerPage, sortFields, debouncedFilters]);

    const {data, loading, error, refetch} = useQuery(PAGINATE_CUSTOMERS, {
        variables,
        fetchPolicy: "no-cache",
    });

    const {data: resellerData} = useQuery(FILTER_RESELLERS, {
        skip: role === "RESELLER",
        fetchPolicy: "no-cache",
    });

    const [createCustomer, {loading: creating}] = useMutation(CREATE_CUSTOMER);
    const [updateCustomer, {loading: updating}] = useMutation(UPDATE_CUSTOMER);
    const [deleteCustomer, {loading: deleting}] = useMutation(DELETE_CUSTOMER);

    const pageInfo = data?.paginateCustomer?.pageInfo;
    const rows = data?.paginateCustomer?.content ?? [];

    const handleChangePage = (_e, newPage) => setPage(newPage);
    const handleChangeRowsPerPage = (e) => {
        setRpp(parseInt(e.target.value, 10));
        setPage(0);
    };


    const handleSearchKeyDown = (e) => {
        if (e.key === "Enter") {
            setDebouncedFilters({
                name: nameFilter.trim(),
                type: typeFilter || "",
                reseller: resellerFilter || "",
            });
            setPage(0);
        }
    };
    const applyFilters = () => {
        setDebouncedFilters({
            name: nameFilter.trim(),
            type: typeFilter || "",
            reseller: resellerFilter || "",
        });
        setPage(0);
    };


    const openEdit = (row) => {
        setEditForm({
            id: row.id,
            name: row.name || "",
            type: row.type || "",
            reseller: row?.reseller?.id || "",
        });
        setEditOpen(true);
    };

    const openDelete = (row) => {
        setDeleteId(row.id);
        setEditForm((prev) => ({...prev, name: row.name}));
        setDeleteOpen(true);
    };

    const handleCloseCreate = () => {
        setCreateOpen(false);
        setCreateError("");
        setCreateApiError({name: "", general: ""});
        setCreateForm({name: "", type: "", reseller: ""});
    };

    const handleCloseEdit = () => {
        setEditOpen(false);
        setEditApiError({name: "", general: ""});
        setEditError("");
    };

    const handleCloseDelete = () => {
        setDeleteOpen(false);
        setDeleteError("");
    };

    const doCreate = async () => {
        setCreateApiError({name: "", general: ""});
        if (!createForm.name.trim() || !createForm.type) {
            setCreateError(t("validation.requiredAll"));
            return;
        }
        if (role !== "RESELLER" && !createForm.reseller.trim()) {
            setCreateError(t("validation.resellerRequired"));
            return;
        }
        const input = {
            name: createForm.name.trim(),
            type: createForm.type,
            ...(role !== "RESELLER" && createForm.reseller ? {reseller: createForm.reseller} : {}),
        };
        try {
            setCreateError("");
            await createCustomer({variables: {input}});
            handleCloseCreate();
            setSnackbar({open: true, message: t("customerPage.createSuccess"), severity: "success"});
            refetch();
        } catch (err) {
            handleGraphQLError(err, setCreateApiError, t);
        }
    };

    const doUpdate = async () => {
        setEditApiError({name: "", general: ""});
        const {id, ...rest} = editForm;
        if (!rest.name.trim() || !rest.type) {
            setEditError(t("validation.requiredAll"));
            return;
        }
        const input = {
            name: rest.name.trim(),
            type: rest.type,
            ...(role !== "RESELLER" && rest.reseller ? {reseller: rest.reseller} : {}),
        };
        try {
            setEditError("");
            await updateCustomer({variables: {id, input}});
            handleCloseEdit();
            setSnackbar({open: true, message: t("customerPage.updateSuccess"), severity: "success"});
            refetch();
        } catch (err) {
            handleGraphQLError(err, setEditApiError, t);
        }
    };

    const doDelete = async () => {
        try {
            setDeleteError("");
            await deleteCustomer({variables: {id: deleteId}});
            handleCloseDelete();
            setSnackbar({open: true, message: t("customerPage.deleteSuccess"), severity: "success"});
            refetch();
        } catch (err) {
            const msg = err?.graphQLErrors?.[0]?.message || t("error.deleteFailed");
            setDeleteError(msg);
        }
    };

    useEffect(() => {
        if (!createOpen) setCreateError("");
    }, [createOpen]);
    useEffect(() => {
        if (!editOpen) setEditError("");
    }, [editOpen]);
    useEffect(() => {
        if (!deleteOpen) setDeleteError("");
    }, [deleteOpen]);

    const onResetFilter = () => {
        setNameFilter("");
        setTypeFilter("");
        setResellerFilter("");
        setDebouncedFilters({name: "", type: "", reseller: ""});
        setPage(0);
    };

    const onRefresh = () => refetch();
    const typeOptions = sortOptionsByLabel([
        {value: "", label: t?.("common.all") || "All"},
        {
            value: "INDIVIDUAL",
            label: t("customerPage.types.individual"),
        },
        {value: "CORPORATE", label: t("customerPage.types.corporate")},
    ], {preserveFirst: true});
    return (
        <Box>
            {/* <Typography variant="h5" gutterBottom>{t("customerPage.title")}</Typography> */}
            <PageHeader
                title={t("customerPage.title")}
                subtitle={t("customerPage.subtitle")}
                icon={<BusinessIcon/>}
            />

            <Paper elevation={3} sx={{borderRadius: 2, overflow: "hidden"}}>
                <Toolbar
                    sx={{
                        px: 2,
                        py: 1,
                        gap: 1,
                        flexWrap: "wrap",
                        borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
                        bgcolor: "background.paper",
                    }}
                >

                    <SearchField
                        label={"resellerPage.searchNamePlaceholder"}
                        value={nameFilter}
                        onChange={(e) => setNameFilter(e.target.value)}
                        onKeyDown={handleSearchKeyDown}
                        placeholder={t("resellerPage.searchNamePlaceholder")}
                        isSmall={isSmall}
                    />


                    <ResponsiveSelect
                        label={t("customerPage.fields.type")}
                        value={typeFilter}
                        onChange={(e) => setTypeFilter(e.target.value)}
                        onClear={() => setTypeFilter("")}
                        options={typeOptions}
                        t={t}
                    />


                    {role !== "RESELLER" && (
                        <ResponsiveSelect
                            label={t("customerPage.fields.reseller")}
                            value={resellerFilter}
                            onChange={(e) => setResellerFilter(e.target.value)}
                            onClear={() => setResellerFilter("")}
                            data={resellerData}
                            dataKey="filterReseller"
                            t={t}
                            isSmall={isSmall}
                        />

                    )}

                    <Button
                        variant="contained"
                        color="primary"
                        startIcon={<SearchIcon/>}
                        onClick={applyFilters}
                        sx={{
                            height: 40,
                            px: 2,
                            textTransform: "none",
                            fontWeight: 500,
                            minWidth: isSmall ? "100%" : "auto",
                        }}
                    >
                        {t("common.search")}
                    </Button>

                    <Button
                        variant="outlined"
                        color="secondary"
                        startIcon={<ClearIcon/>}
                        onClick={onResetFilter}
                        sx={{
                            height: 40,
                            px: 2,
                            textTransform: "none",
                            fontWeight: 500,
                            minWidth: isSmall ? "100%" : "auto",
                        }}
                    >
                        {t("common.clear")}
                    </Button>


                    <Box sx={{flex: 1}}/>


                    <Tooltip title={t("common.refresh")}>
            <span>
              <IconButton onClick={onRefresh} aria-label="refresh">
                <RefreshIcon/>
              </IconButton>
            </span>
                    </Tooltip>

                    <Button variant="contained" startIcon={<AddIcon/>} onClick={() => setCreateOpen(true)}>
                        {t("customerPage.createButton")}
                    </Button>
                </Toolbar>

                {loading ? (
                    <Box sx={{p: 4, textAlign: "center"}}>
                        <CircularProgress/>
                    </Box>
                ) : error ? (
                    <Box sx={{p: 2}}>
                        <Alert severity="error">{t("common.error")} {error.message}</Alert>
                    </Box>
                ) : (
                    <>
                        <TableContainer sx={{maxHeight: 520}}>
                            <Table stickyHeader>
                                <TableHead>
                                    <TableRow>

                                        <EllipsisCell sx={{
                                            bgcolor: "grey.50",
                                            fontWeight: 600,
                                            fontSize: "0.875rem",
                                            borderBottom: "2px solid",
                                            borderBottomColor: "divider",
                                            color: "text.primary"
                                        }}>
                                            <TableSortLabel
                                                {...getSortProps("name")}
                                                onClick={() => handleSort("name")}
                                            >
                                                {t("customerPage.table.name")}
                                            </TableSortLabel>
                                        </EllipsisCell>


                                        <EllipsisCell sx={{
                                            bgcolor: "grey.50",
                                            fontWeight: 600,
                                            fontSize: "0.875rem",
                                            borderBottom: "2px solid",
                                            borderBottomColor: "divider",
                                            color: "text.primary"
                                        }}>
                                            <TableSortLabel
                                                {...getSortProps("type")}
                                                onClick={() => handleSort("type")}
                                            >
                                                {t("customerPage.table.type")}
                                            </TableSortLabel>
                                        </EllipsisCell>

                                        {role !== "RESELLER" && (
                                            <EllipsisCell sx={{
                                                bgcolor: "grey.50",
                                                fontWeight: 600,
                                                fontSize: "0.875rem",
                                                borderBottom: "2px solid",
                                                borderBottomColor: "divider",
                                                color: "text.primary"
                                            }}>
                                                <TableSortLabel
                                                    {...getSortProps("resellerName")}
                                                    onClick={() => handleSort("resellerName")}
                                                >
                                                    {t("customerPage.table.reseller")}
                                                </TableSortLabel>
                                            </EllipsisCell>
                                        )}

                                        <EllipsisCell sx={{
                                            bgcolor: "grey.50",
                                            fontWeight: 600,
                                            fontSize: "0.875rem",
                                            borderBottom: "2px solid",
                                            borderBottomColor: "divider",
                                            color: "text.primary",
                                            width: "1%",
                                            whiteSpace: "nowrap"
                                        }}>
                                            <TableSortLabel
                                                {...getSortProps("updatedTimestamp")}
                                                onClick={() => handleSort("updatedTimestamp")}
                                            >
                                                {t("userPage.table.updated")}
                                            </TableSortLabel>
                                        </EllipsisCell>

                                        <EllipsisCell sx={{
                                            bgcolor: "grey.50",
                                            fontWeight: 600,
                                            fontSize: "0.875rem",
                                            borderBottom: "2px solid",
                                            borderBottomColor: "divider",
                                            color: "text.primary",
                                            width: "1%",
                                            whiteSpace: "nowrap"
                                        }}>
                                            {t("userPage.table.actions")}
                                        </EllipsisCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {rows.length === 0 ? (
                                        <TableRow>
                                            <EllipsisCell colSpan={emptyStateColumns} align="center">{t("userPage.noRecords")}</EllipsisCell>
                                        </TableRow>
                                    ) : (
                                        rows.map((r) => (
                                            <TableRow
                                                key={r.id}
                                                hover
                                                sx={{
                                                    "&:nth-of-type(even)": {
                                                        bgcolor: "grey.50"
                                                    },
                                                    "&:hover": {
                                                        bgcolor: "action.hover"
                                                    }
                                                }}
                                            >
                                                <EllipsisCell>
                                                    {displayValue(r.name)}
                                                </EllipsisCell>

                                                <EllipsisCell>
                                                    {displayValue(
                                                        r.type === "CORPORATE"
                                                            ? t("customerPage.types.corporate")
                                                            : r.type
                                                                ? t("customerPage.types.individual")
                                                                : ""
                                                    )}
                                                </EllipsisCell>
                                                {role !== "RESELLER" && (
                                                    <EllipsisCell>
                                                        {displayValue(r.reseller?.name)}
                                                    </EllipsisCell>
                                                )}
                                                <EllipsisCell sx={{whiteSpace: "nowrap"}}>
                                                    {r.updatedTimestamp ? formatToLocalTime(r.updatedTimestamp) : "-"}
                                                </EllipsisCell>
                                                <EllipsisCell sx={{whiteSpace: "nowrap"}}>
                                                    <Tooltip title={t("common.update")}>
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => openEdit(r)}
                                                        >
                                                            <EditIcon/>
                                                        </IconButton>
                                                    </Tooltip>
                                                    <Tooltip title={t("common.delete")}>
                                                        <IconButton
                                                            size="small"
                                                            color="error"
                                                            onClick={() => openDelete(r)}
                                                        >
                                                            <DeleteIcon/>
                                                        </IconButton>
                                                    </Tooltip>
                                                </EllipsisCell>
                                            </TableRow>
                                        ))
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>

                        <TablePagination
                            labelRowsPerPage={t("common.rowsPerPage")}
                            component="div"
                            rowsPerPageOptions={[10, 25, 50, 100]}
                            count={pageInfo?.totalElements ?? 0}
                            rowsPerPage={pageInfo?.pageSize ?? rowsPerPage}
                            page={pageInfo?.pageNumber ?? page}
                            onPageChange={handleChangePage}
                            onRowsPerPageChange={handleChangeRowsPerPage}
                            ActionsComponent={TablePaginationActions}
                        />
                    </>
                )}
            </Paper>


            <Dialog open={createOpen} onClose={handleCloseCreate} fullWidth maxWidth="sm">
                <DialogTitle sx={{fontWeight: "bold"}}>{t("customerPage.createTitle")}</DialogTitle>
                <Divider sx={{mb: 1, borderColor: "divider", opacity: 0.7}}/>
                {createApiError.general && <Alert severity="error" sx={{mb: 2}}>{createApiError.general}</Alert>}
                <DialogContent sx={{pt: 1, overflow: "visible"}}>
                    <Stack spacing={2} sx={{mt: 1}}>
                        <TextField
                            label={t("customerPage.fields.name")}
                            value={createForm.name}
                            onChange={(e) => setCreateForm({...createForm, name: e.target.value})}
                            error={!!createApiError.name || (createError && !createForm.name.trim())}
                            helperText={
                                createApiError.name ||
                                (createError && !createForm.name.trim() ? t("validation.requiredField") : "")
                            }
                            autoFocus
                        />


                        <ResponsiveSelect
                            fullWidth={true}
                            label={t("customerPage.fields.type")}
                            options={typeOptions}
                            t={t}
                            value={createForm.type}
                            onChange={(e) => setCreateForm({...createForm, type: e.target.value})}
                            error={!!createError && !createForm.type}
                            helperText={createError && !createForm.type ? t("validation.requiredField") : ""}
                        />


                        {role !== "RESELLER" && (

                            <ResponsiveSelect
                                fullWidth={true}
                                label={t("customerPage.fields.reseller")}
                                data={resellerData}
                                dataKey={"filterReseller"}
                                value={createForm.reseller}
                                onChange={(e) => setCreateForm({...createForm, reseller: e.target.value})}
                                error={!!createError && !createForm.reseller.trim()}
                                helperText={createError && !createForm.reseller.trim() ? t("validation.resellerRequired") : ""}
                            />

                            // <TextField
                            //   select
                            //   label={t("customerPage.fields.reseller")}
                            //   value={createForm.reseller}
                            //   onChange={(e) => setCreateForm({ ...createForm, reseller: e.target.value })}
                            //   error={!!createError && !createForm.reseller.trim()}
                            //   helperText={createError && !createForm.reseller.trim() ? t("validation.resellerRequired") : ""}
                            // >
                            //   {(resellerData?.filterReseller ?? []).map((r) => (
                            //     <MenuItem key={r.id} value={r.id}>{r.name}</MenuItem>
                            //   ))}
                            // </TextField>
                        )}
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseCreate}>{t("common.cancel")}</Button>
                    <Button onClick={doCreate} disabled={creating} aria-busy={creating} variant="contained">
                        {creating ? <CircularProgress size={20}/> : t("common.create")}
                    </Button>
                </DialogActions>
            </Dialog>


            <Dialog open={editOpen} onClose={handleCloseEdit} fullWidth maxWidth="sm">
                <DialogTitle sx={{fontWeight: "bold"}}>{t("customerPage.updateTitle")}</DialogTitle>
                <Divider sx={{mb: 1, borderColor: "divider", opacity: 0.7}}/>
                {editApiError.general && <Alert severity="error" sx={{mb: 2}}>{editApiError.general}</Alert>}
                <DialogContent sx={{pt: 1}}>
                    <Stack spacing={2} sx={{mt: 1}}>
                        <TextField
                            label={t("customerPage.fields.name")}
                            value={editForm.name}
                            onChange={(e) => setEditForm({...editForm, name: e.target.value})}
                            error={!!editApiError.name || (editError && !editForm.name.trim())}
                            helperText={
                                editApiError.name ||
                                (editError && !editForm.name.trim() ? t("validation.requiredField") : "")
                            }
                            autoFocus
                        />


                        <ResponsiveSelect
                            fullWidth={true}
                            label={t("customerPage.fields.type")}
                            options={typeOptions}
                            t={t}
                            value={editForm.type}
                            onChange={(e) => setEditForm({...editForm, type: e.target.value})}
                            error={!!editError && !editForm.type}
                            helperText={editError && !editForm.type ? t("validation.requiredField") : ""}
                        />


                        {role !== "RESELLER" && (

                            <ResponsiveSelect
                                label={t("customerPage.fields.reseller")}
                                value={editForm.reseller}
                                onChange={(e) => setEditForm({...editForm, reseller: e.target.value})}
                                data={resellerData}
                                dataKey="filterReseller"
                                fullWidth={true}
                            />


                            // <TextField
                            //   select
                            //   label={t("customerPage.fields.reseller")}
                            //   value={editForm.reseller}
                            //   onChange={(e) => setEditForm({ ...editForm, reseller: e.target.value })}
                            // >
                            //   {(resellerData?.filterReseller ?? []).map((r) => (
                            //     <MenuItem key={r.id} value={r.id}>{r.name}</MenuItem>
                            //   ))}
                            // </TextField>


                        )}
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseEdit}>{t("common.cancel")}</Button>
                    <Button onClick={doUpdate} disabled={updating} aria-busy={updating} variant="contained">
                        {updating ? <CircularProgress size={20}/> : t("common.update")}
                    </Button>
                </DialogActions>
            </Dialog>


            <Dialog open={deleteOpen} onClose={handleCloseDelete}>
                <DialogTitle sx={{fontWeight: "bold"}}>{t("customerPage.deleteConfirmTitle")}</DialogTitle>
                <Divider sx={{mb: 1, borderColor: "divider", opacity: 0.7}}/>
                {deleteError && <Alert severity="error" sx={{mb: 2}}>{deleteError}</Alert>}
                <DialogContent>
                    <Typography variant="h6" sx={{color: "#d32f2f", fontWeight: "bold", mt: 1}}>
                        {editForm.name}
                    </Typography>
                    <Typography>{t("customerPage.deleteConfirmMessage")}</Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDelete}>{t("common.cancel")}</Button>
                    <Button onClick={doDelete} color="error" disabled={deleting} aria-busy={deleting} variant="contained">
                        {deleting ? <CircularProgress size={20}/> : t("common.delete")}
                    </Button>
                </DialogActions>
            </Dialog>


            <Snackbar
                TransitionComponent={SlideUp}
                open={snackbar.open}
                autoHideDuration={2500}
                onClose={() => setSnackbar({...snackbar, open: false})}
                anchorOrigin={{vertical: "bottom", horizontal: "center"}}
                sx={{
                    "& .MuiAlert-root": {
                        borderRadius: 2,
                        boxShadow: "0px 4px 12px rgba(0,0,0,0.25)",
                        fontWeight: 500,
                        minWidth: 260,
                        justifyContent: "center",
                    },
                }}
            >
                <Alert
                    severity={snackbar.severity}
                    onClose={() => setSnackbar({...snackbar, open: false})}
                    variant="filled"
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default Customer;
